import balloonInflateSoundSrc from '@/assets/sounds/balloon_inflate.mp3';
import balloonPopSoundSrc from '@/assets/sounds/balloon_pop.mp3';
import flipSoundSrc from '@/assets/sounds/flip.mp3';

export type SoundConfig = {
	src: string | string[];
	volume?: number;
};

export const CASINO_CONFIG = {
	global: {
		defaultBetAmount: 1,
		minBet: 0.1,
		maxBet: 100,
	},
	coinflip: {
		sides: {
			HEADS: 0,
			TAILS: 1,
		},
		bet: {
			min: 0.1,
			max: 200,
		},
		gasBudget: 10_000_000, // 0.1 SUI
		sounds: {
			flip: {
				src: flipSoundSrc,
				volume: 0.5,
			},
		},
	},
	dice: {
		bet: {
			min: 0.1,
			max: 100,
		},
		roll: {
			min: 6,
			max: 97,
			defaultWinChance: 50,
			maxGeneratedNumber: 100,
		},
		rtp: {
			min: 0.95,
			max: 0.98,
		},
		maxPayout: 300,
	},
	plinko: {
		bet: {
			min: 0.1,
			max: 100,
		},
		rows: {
			default: 6,
			min: 6,
			max: 16,
		},
		defaultRisk: 'medium',
		riskLevels: ['low', 'medium'] as const,
		simulation: {
			width: 1126,
			height: 950,
			enabled: false,
		},
	},
	limbo: {
		bet: {
			min: 0.1,
			max: 300,
			maxPayout: 400,
			minMultiplier: 1.01,
			maxMultiplier: 100,
		},
		display: {
			defaultTargetMultiplier: 2,
			minDisplayedMultiplier: 1,
		},
		rtp: {
			min: 0.95,
			max: 0.95,
		},
		gasBudget: 0.01,
		animation: {
			scaleFactor: 0.2,
			speedBurstChance: 0.1,
			defaultSpeed: 8,
			defaultIncrement: 0.01,
			maxIncrement: 1.41,
			incrementStep: 0.01,
			frames: {
				pumpingStart: 60,
				pumpingEnd: 145,
				winStart: 172,
				winEnd: 264,
				loseStart: 322,
				loseEnd: 431,
			},
		},
		sounds: {
			loss: {
				src: balloonPopSoundSrc,
				volume: 0.1,
			},
			inflate: {
				src: balloonInflateSoundSrc,
				volume: 0.05,
			},
		},
	},
	suiroyale: {
		bet: {
			min: 0.1,
			maxProfit: 100,
			minMultiplier: 1.01,
			maxMultiplier: 100,
		},
		display: {
			defaultTargetMultiplier: 2,
			minDisplayedMultiplier: 1,
		},
		rtp: {
			min: 0.96,
			max: 0.97,
		},
		gasBudget: 0.01,
		animation: {
			scaleFactor: 0.2,
			speedBurstChance: 0.1,
			defaultSpeed: 8,
			defaultIncrement: 0.01,
			maxIncrement: 1.41,
			incrementStep: 0.01,
		},
		sounds: {
			loss: {
				src: balloonPopSoundSrc,
				volume: 0.1,
			},
			increment: {
				src: balloonInflateSoundSrc,
				volume: 0.05,
			},
		},
	},
} as const;

export type CasinoGameKey = Exclude<keyof typeof CASINO_CONFIG, 'global'>;

export type GameSettings<T extends CasinoGameKey> = (typeof CASINO_CONFIG)[T];

export const getGameConfig = <T extends CasinoGameKey>(game: T) => CASINO_CONFIG[game];

type GameWithGasBudget = { gasBudget?: number };
type GameWithSounds = { sounds?: Record<string, SoundConfig> };

export const getGameGasBudget = (game: CasinoGameKey) => {
	const config = CASINO_CONFIG[game] as GameWithGasBudget;
	return config.gasBudget ?? null;
};

export const getGameSoundConfig = (game: CasinoGameKey, soundKey: string): SoundConfig | undefined => {
	const config = CASINO_CONFIG[game] as GameWithSounds;
	return config.sounds?.[soundKey];
};
